// ═════════════════════════════════════════════════════════════════════════════
// BOILING OFFERS — the platform, after the rebuild (2026-08-03)
//
// The same shape as Falcon, on purpose. That is the point of the decision: the
// Tailormind standard is the pages store + the MCP seam + an omnigent agent,
// and it should look identical from a distance whichever client it serves.
//
// ── CORRECTED THE SAME EVENING IT WAS DRAWN ─────────────────────────────────
// The first version put a bespoke OUTBOUND GATE inside our application, ported
// from the old system. Erez asked the right question — is that not just
// permissions, and does omnigent not already have it? It does, and better:
//
//   · SIX enforcement phases, not one — request, response, tool call, tool
//     result, LLM request, LLM response.
//   · Three verdicts — ALLOW / DENY / ASK, where ASK becomes a real approval
//     card (an MCP-style elicitation carrying a message and a schema).
//   · Fail-closed on tool calls and requests.
//   · Every policy is handed cumulative spend, actor identity and trajectory.
//   · A shipped PII policy that inspects content, and `prompt_policy` — an LLM
//     classifier registered with one sentence of English.
//
// So the gate is not ours to build, and it is drawn where it actually lives:
// inside omnigent, spanning the runtime. A bespoke gate only ever sees what
// passes through it; a policy sees every phase of every turn.
//
// And PHASE 1 needs none of it. Lenny talks to the TEAM, as a labelled bot,
// giving reports. Partners arrive in phase 2, and that is when a response-phase
// policy gets written — against a live system we understand, not guessed now.
//
// Content rules from skill://diagrams: two to four words a node, icon leads,
// containment is real, seven boxes at the top level. Nothing is hand-placed —
// c4.tsx measures bottom-up and places top-down.
// ═════════════════════════════════════════════════════════════════════════════

import { Canvas, leaf, person, box, type Diagram } from "./c4";

const PLATFORM: Diagram = (() => {
  // Who touches it. Matan runs the business; Daniel runs the day to day; the
  // partners are OUTSIDE the company, which is the fact the whole right-hand
  // side of this diagram exists to handle.
  const actors = box("bp-act", undefined, "col", [
    person("bp-matan", "Matan"),
    person("bp-daniel", "Daniel"),
    person("bp-partners", "Advertisers · affiliates"),
  ], { gap: 12 });

  const doors = box("bp-doors", "DOORS", "col", [
    leaf("bp-tg", "Telegram", "[omni-boiling]", "Send"),
    leaf("bp-biz", "Telegram Business", "[phase 2]", "UserCheck"),
    leaf("bp-web", "Boiling console", "[the same shell]", "LayoutDashboard"),
  ], { gap: 12 });

  const screens = box("bp-screens", "THE SCREENS", "col", [
    box("bp-work", "The business", "row", [
      leaf("bp-leads", "Leads", undefined, "Activity"),
      leaf("bp-part", "Partners", undefined, "Users"),
      leaf("bp-chats", "Chats", undefined, "MessagesSquare"),
    ], { depth: 1, gap: 12 }),
    box("bp-mind", "The agent's mind", "row", [
      leaf("bp-mem", "Memory", undefined, "Brain"),
      leaf("bp-kb", "Knowledge", undefined, "BookOpen"),
      leaf("bp-skill", "Procedures", undefined, "ListChecks"),
    ], { depth: 1, gap: 12 }),
    leaf("bp-appr", "Approvals", "[omnigent elicitations]", "Inbox"),
  ], { gap: 12 });

  const ours = box("bp-ours", "OUR APPLICATION — boiling-app", "col", [
    box("bp-doorsvc", "Two doors, one read layer", "row", [
      leaf("bp-api", "/api", "[the screens]", "Server"),
      leaf("bp-mcp", "/mcp", "[named tools · bearer]", "Plug"),
    ], { depth: 1, gap: 12 }),
    box("bp-store", "The pages store", "row", [
      leaf("bp-pages", "pages", "[memory · kb · skill]", "FileText"),
      leaf("bp-evals", "evals", "[cases · checks]", "CheckCheck"),
    ], { depth: 1, gap: 12 }),
    leaf("bp-bdb", "Boiling Postgres", "[one tenant, one box]", "Database"),
  ], { gap: 12 });

  const theirs = box("bp-theirs", "OMNIGENT — unmodified", "col", [
    leaf("bp-srv", "Server", "[sessions · auth]", "Boxes"),
    leaf("bp-run", "Runner", "[the agent loop]", "Cpu"),
    leaf("bp-sched", "Scheduled tasks", "[the hourly wake]", "Clock"),
    // Where the gate actually lives, and why we are not writing one.
    leaf("bp-pol", "Policies", "[6 phases · allow·deny·ask]", "ShieldCheck", "gate"),
  ], { gap: 12 });

  const source = box("bp-src", "THE SOURCE OF TRUTH", "col", [
    leaf("bp-botrack", "Botrack", "[leads · CRM statuses]", "Radio"),
  ], { gap: 12 });

  return {
    key: "bp",
    title: "Boiling Offers — the platform",
    subtitle: "The same standard as Falcon · one box, one client · phase 1 is the team, not partners · v1.1 · 2026-08-03",
    padBottom: 84,
    cols: [
      { root: actors, align: "center" },
      { root: doors, align: "center" },
      { root: screens, align: "center" },
      { root: ours, align: "center" },
      { root: theirs, align: "center" },
      { root: source, align: "center" },
    ],
    wires: [
      { a: "bp-matan", b: "bp-tg", as: "r", bs: "l", label: "asks · is told" },
      { a: "bp-partners", b: "bp-biz", as: "r", bs: "l", label: "phase 2", dashed: true },
      { a: "bp-daniel", b: "bp-web", as: "r", bs: "l", label: "HTTPS" },
      { a: "bp-doors", b: "bp-screens", as: "r", bs: "l", label: "HTTPS" },
      { a: "bp-screens", b: "bp-api", as: "r", bs: "l", label: "HTTPS + SSE" },
      { a: "bp-run", b: "bp-mcp", as: "b", bs: "b", label: "MCP — THE ONE SEAM" },
      { a: "bp-sched", b: "bp-run", as: "l", bs: "l", label: "wakes it hourly", dashed: true },
      { a: "bp-pol", b: "bp-run", as: "t", bs: "r", label: "every phase of every turn", red: true },
      { a: "bp-botrack", b: "bp-bdb", as: "b", bs: "b", label: "sync", dashed: true },
    ],
    legend: [
      { style: "solid", label: "request path" },
      { style: "dash", label: "what runs without being asked, and what is phase 2" },
      { style: "red", label: "where a turn can be denied or held for a human — omnigent\u2019s own policies" },
    ],
  };
})();

export function BoilingPlatformDiagram() {
  return <Canvas d={PLATFORM} mode="contrast" />;
}
